/**
 * src/components/spatial/spatialAdminList.ts
 *
 * Pure list logic for the Manage Spatial Objects panel: viewport
 * scoping, distance sorting, text search, country resolution and
 * parent/child tree grouping.
 *
 * This is still client-side, and deliberately so. GeoLink shipped
 * server-side `bbox`, `near`, `q` and `sort` on 2026-07-30, but the
 * Azure deployment predates that and drops those parameters silently.
 * The panel probes `SpatialObjectAdminPage.serverFiltered` and passes
 * `alreadyFiltered` here to skip the redundant pass when the server did
 * the work; against an older build the in-memory path is the only thing
 * standing between the operator and a plausible-but-unfiltered list.
 *
 * Sorting stays here in both cases. `view` (in-view first, then
 * distance) has no server equivalent, and re-sorting a page of rows is
 * cheap.
 *
 * Delete the fallback once Azure is on the new build — see
 * `docs/geolink-spatial-admin-and-organizations.md` for the original
 * change request and `docs/geolink-organizations-followup.md` §C1 for
 * why we probe rather than assume.
 */

import type {
  SpatialObjectAdminRow,
  SpatialObjectAdminSort,
} from '../../services/spatial/geoLinkSpatialObjectsAdminClient';
import type { SpatialObjectType } from '../../services/spatial/geoLinkTypes';

// ─── Types ────────────────────────────────────────────────────────────

export interface LngLat { lng: number; lat: number }

export interface ListBounds {
  west: number;
  south: number;
  east: number;
  north: number;
}

/**
 * App's country resolver (`resolveCountryRef` in `src/App.tsx`) — a
 * synchronous point-in-polygon lookup against the countries GeoJSON the
 * main map already has loaded. Passing it in keeps the country column
 * network-free; GeoLink does not return `country_code` on admin rows
 * even though the column exists.
 */
export type ResolveCountry = (
  lng: number,
  lat: number,
  nearest?: boolean,
) => { code: number; name: string; iso2: string | null } | null;

export type SpatialSortKey = 'view' | 'distance' | 'name' | 'type' | 'updated';

export interface SpatialListFilters {
  search: string;
  type: SpatialObjectType | '';
  owner: string;
  mineOnly: boolean;
  walletAddress: string | null;
  countryIso2: string;
  inViewOnly: boolean;
  sort: SpatialSortKey;
}

/** A row plus everything the list UI needs that isn't on the wire. */
export interface DecoratedRow {
  row: SpatialObjectAdminRow;
  point: LngLat | null;
  inView: boolean;
  /** Metres from the map centre. Null when the row has no resolvable point. */
  distanceM: number | null;
  countryIso2: string | null;
  countryName: string | null;
  /** Children across ALL loaded rows, not just the filtered set. */
  childCount: number;
}

// ─── Object types ─────────────────────────────────────────────────────

export const OBJECT_TYPES: { id: SpatialObjectType; label: string }[] = [
  { id: 'building',                   label: 'Building' },
  { id: 'floor',                      label: 'Floor' },
  { id: 'zone',                       label: 'Zone' },
  { id: 'slice',                      label: 'Slice' },
  { id: 'object_3d',                  label: '3D object' },
  { id: 'real_estate',                label: 'Parcel (real estate)' },
  { id: 'event_object',               label: 'Event' },
  { id: 'contract_execution_object',  label: 'Trigger' },
  { id: 'surface_zone',               label: 'Surface (facade)' },
];

export const OBJECT_TYPE_LABEL: Record<string, string> = OBJECT_TYPES.reduce(
  (acc, t) => { acc[t.id] = t.label; return acc; },
  {} as Record<string, string>,
);

export function objectTypeLabel(type: string | null | undefined): string {
  if (!type) return '—';
  return OBJECT_TYPE_LABEL[type] ?? type;
}

// ─── Parent / child rules ─────────────────────────────────────────────

/**
 * Which object types may act as the parent of a given child type. Used
 * to keep the parent picker short and meaningful — a floor belongs to a
 * building or a parcel, never to another floor.
 */
export const PARENT_TYPES_FOR: Partial<Record<SpatialObjectType, SpatialObjectType[]>> = {
  floor:                     ['building', 'real_estate'],
  slice:                     ['building', 'floor', 'real_estate'],
  zone:                      ['building', 'floor', 'real_estate', 'event_object'],
  event_object:              ['building', 'floor', 'zone', 'real_estate'],
  contract_execution_object: ['building', 'floor', 'zone', 'real_estate', 'event_object'],
  object_3d:                 ['building', 'floor', 'zone', 'real_estate'],
  real_estate:               ['real_estate'],
  building:                  ['real_estate'],
  // A surface is a face of something. Standing on its own it would have
  // nothing to be projected onto.
  surface_zone:              ['building', 'floor', 'real_estate', 'object_3d'],
};

export type ParentRequirement = 'required' | 'recommended' | 'optional';

/**
 * A floor or slice is meaningless without the structure it sits in, so
 * we hard-require a parent. Zones are usually inside something but can
 * legitimately stand alone (a public plaza), so they only warn.
 */
export function parentRequirement(type: SpatialObjectType): ParentRequirement {
  if (type === 'floor' || type === 'slice' || type === 'surface_zone') return 'required';
  if (type === 'zone') return 'recommended';
  return 'optional';
}

/** The type a new child of `parentType` most likely wants to be. */
export function defaultChildType(parentType: SpatialObjectType | undefined): SpatialObjectType {
  switch (parentType) {
    case 'building':    return 'floor';
    case 'real_estate': return 'building';
    case 'floor':       return 'zone';
    case 'zone':        return 'contract_execution_object';
    case 'event_object':return 'zone';
    default:            return 'zone';
  }
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** GeoLink rejects non-UUID parent ids server-side; catch it in the form. */
export function isUuid(value: string): boolean {
  return UUID_RE.test(value.trim());
}

// ─── Geometry helpers ─────────────────────────────────────────────────

/** Walk any GeoJSON coordinate nesting and accumulate a bbox. */
function accumulateBounds(coords: unknown, acc: number[]): void {
  if (!Array.isArray(coords)) return;
  if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
    const lng = coords[0] as number;
    const lat = coords[1] as number;
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
    if (lng < acc[0]) acc[0] = lng;
    if (lat < acc[1]) acc[1] = lat;
    if (lng > acc[2]) acc[2] = lng;
    if (lat > acc[3]) acc[3] = lat;
    return;
  }
  for (const c of coords) accumulateBounds(c, acc);
}

export function geometryBounds(geometry: unknown): ListBounds | null {
  if (!geometry || typeof geometry !== 'object') return null;
  const g = geometry as { type?: string; coordinates?: unknown; geometries?: unknown[] };
  const acc = [Infinity, Infinity, -Infinity, -Infinity];
  if (Array.isArray(g.geometries)) {
    for (const child of g.geometries) {
      const inner = geometryBounds(child);
      if (!inner) continue;
      if (inner.west  < acc[0]) acc[0] = inner.west;
      if (inner.south < acc[1]) acc[1] = inner.south;
      if (inner.east  > acc[2]) acc[2] = inner.east;
      if (inner.north > acc[3]) acc[3] = inner.north;
    }
  } else {
    accumulateBounds(g.coordinates, acc);
  }
  if (!Number.isFinite(acc[0])) return null;
  return { west: acc[0], south: acc[1], east: acc[2], north: acc[3] };
}

/**
 * Representative point for a row. Prefers the PostGIS-computed
 * `centroid` GeoLink already returns, falling back to the centre of the
 * geometry bbox for rows saved before centroids were backfilled.
 */
export function rowPoint(row: SpatialObjectAdminRow): LngLat | null {
  const c = row.centroid;
  if (c && Array.isArray(c.coordinates)) {
    const [lng, lat] = c.coordinates;
    if (Number.isFinite(lng) && Number.isFinite(lat)) return { lng, lat };
  }
  const b = geometryBounds(row.geometry);
  if (!b) return null;
  return { lng: (b.west + b.east) / 2, lat: (b.south + b.north) / 2 };
}

export function pointInBounds(p: LngLat, b: ListBounds): boolean {
  if (p.lat < b.south || p.lat > b.north) return false;
  // Viewports that cross the antimeridian have west > east.
  if (b.west <= b.east) return p.lng >= b.west && p.lng <= b.east;
  return p.lng >= b.west || p.lng <= b.east;
}

const EARTH_RADIUS_M = 6_371_008.8;

export function distanceMeters(a: LngLat, b: LngLat): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Compact "1.2 km" / "340 m" for the list rows. */
export function formatDistance(meters: number | null): string {
  if (meters == null || !Number.isFinite(meters)) return '';
  if (meters < 1000) return `${Math.round(meters)} m`;
  if (meters < 100_000) return `${(meters / 1000).toFixed(1)} km`;
  return `${Math.round(meters / 1000)} km`;
}

// ─── Decoration ───────────────────────────────────────────────────────

export interface DecorateOptions {
  bounds: ListBounds | null;
  center: LngLat | null;
  resolveCountry?: ResolveCountry | null;
}

export function decorateRows(
  rows: SpatialObjectAdminRow[],
  opts: DecorateOptions,
): DecoratedRow[] {
  const childCounts = new Map<string, number>();
  for (const r of rows) {
    const pid = r.parentObjectId;
    if (pid) childCounts.set(pid, (childCounts.get(pid) ?? 0) + 1);
  }

  return rows.map((row) => {
    const point = rowPoint(row);
    // GeoLink returns `countryCode` since 2026-07-30. Prefer it: it is
    // what the database actually stores and filters on, so resolving
    // locally could disagree with a `country_code` query. Fall back to
    // the point-in-polygon resolver for older builds and for rows whose
    // country was never set.
    let countryIso2: string | null = row.countryCode
      ? row.countryCode.trim().toUpperCase() || null
      : null;
    let countryName: string | null = null;
    if (point && opts.resolveCountry) {
      try {
        const hit = opts.resolveCountry(point.lng, point.lat);
        if (hit) {
          if (!countryIso2) countryIso2 = hit.iso2 ? hit.iso2.toUpperCase() : null;
          // The resolver is still the only source of a display name;
          // GeoLink returns the code alone.
          if (!hit.iso2 || !countryIso2 || hit.iso2.toUpperCase() === countryIso2) {
            countryName = hit.name ?? null;
          }
        }
      } catch {
        // A resolver failure must never break the list.
      }
    }
    return {
      row,
      point,
      inView: !!(point && opts.bounds && pointInBounds(point, opts.bounds)),
      distanceM: point && opts.center ? distanceMeters(opts.center, point) : null,
      countryIso2,
      countryName,
      childCount: childCounts.get(row.id) ?? 0,
    };
  });
}

// ─── Filtering + sorting ──────────────────────────────────────────────

function matchesSearch(
  d: DecoratedRow,
  needle: string,
  nameById: Map<string, string>,
): boolean {
  if (!needle) return true;
  const r = d.row;
  const parentName = r.parentObjectId ? nameById.get(r.parentObjectId) ?? '' : '';
  const haystack = [
    r.name ?? '',
    r.id,
    r.objectType ?? '',
    objectTypeLabel(r.objectType),
    r.ownerWallet ?? '',
    r.description ?? '',
    parentName,
    d.countryIso2 ?? '',
    d.countryName ?? '',
  ];
  return haystack.some((h) => h.toLowerCase().includes(needle));
}

function compareRows(a: DecoratedRow, b: DecoratedRow, sort: SpatialSortKey): number {
  const byName = () =>
    (a.row.name ?? a.row.id).localeCompare(b.row.name ?? b.row.id, undefined, {
      sensitivity: 'base',
    });
  // Rows with no resolvable point sink to the bottom of distance sorts.
  const byDistance = () => {
    const da = a.distanceM ?? Number.POSITIVE_INFINITY;
    const db = b.distanceM ?? Number.POSITIVE_INFINITY;
    return da === db ? byName() : da - db;
  };

  switch (sort) {
    case 'view':
      if (a.inView !== b.inView) return a.inView ? -1 : 1;
      return byDistance();
    case 'distance':
      return byDistance();
    case 'type': {
      const ta = objectTypeLabel(a.row.objectType);
      const tb = objectTypeLabel(b.row.objectType);
      return ta === tb ? byName() : ta.localeCompare(tb);
    }
    case 'updated': {
      const ua = Date.parse(a.row.updatedAt ?? '') || 0;
      const ub = Date.parse(b.row.updatedAt ?? '') || 0;
      return ua === ub ? byName() : ub - ua;
    }
    case 'name':
    default:
      return byName();
  }
}

/**
 * @param alreadyFiltered Set when GeoLink applied the same predicates
 *   server-side (`SpatialObjectAdminPage.serverFiltered`). Filtering is
 *   then skipped and only the sort runs.
 *
 *   Skipping matters for more than speed: our in-memory search matches a
 *   substring anywhere in the id, while GeoLink matches an id *prefix*.
 *   Running both would quietly apply the stricter of the two, so a
 *   server-side result set would lose rows the server deliberately
 *   included.
 */
export function filterAndSortRows(
  decorated: DecoratedRow[],
  filters: SpatialListFilters,
  alreadyFiltered = false,
): DecoratedRow[] {
  if (alreadyFiltered) {
    return decorated.slice().sort((a, b) => compareRows(a, b, filters.sort));
  }

  const needle = filters.search.trim().toLowerCase();
  const owner = filters.mineOnly
    ? (filters.walletAddress ?? '').trim()
    : filters.owner.trim();
  const nameById = new Map<string, string>();
  for (const d of decorated) nameById.set(d.row.id, d.row.name ?? '');

  const out = decorated.filter((d) => {
    if (filters.type && d.row.objectType !== filters.type) return false;
    if (owner && (d.row.ownerWallet ?? '') !== owner) return false;
    if (filters.countryIso2 && d.countryIso2 !== filters.countryIso2) return false;
    if (filters.inViewOnly && !d.inView) return false;
    if (!matchesSearch(d, needle, nameById)) return false;
    return true;
  });

  return out.sort((a, b) => compareRows(a, b, filters.sort));
}

/**
 * Translate the panel's sort into GeoLink's `sort` parameter.
 *
 * Only `name` and `updated` have a server equivalent. `view` and
 * `distance` are relative to the map centre, and `type` sorts by our
 * display labels rather than the stored enum, so both stay client-side
 * — we let GeoLink default to `created_at` and re-sort the page. That is
 * only lossy when the result set overflows a page, where "the newest
 * 1000, re-sorted" is the best available answer anyway.
 */
export function serverSortFor(sort: SpatialSortKey): SpatialObjectAdminSort | undefined {
  switch (sort) {
    case 'name':    return 'name';
    case 'updated': return 'updated_at';
    default:        return undefined;
  }
}

/** Distinct countries present in the loaded set, for the filter dropdown. */
export function countryOptions(
  decorated: DecoratedRow[],
): { iso2: string; name: string; count: number }[] {
  const byIso = new Map<string, { iso2: string; name: string; count: number }>();
  for (const d of decorated) {
    if (!d.countryIso2) continue;
    const existing = byIso.get(d.countryIso2);
    if (existing) existing.count += 1;
    else byIso.set(d.countryIso2, { iso2: d.countryIso2, name: d.countryName ?? d.countryIso2, count: 1 });
  }
  return Array.from(byIso.values()).sort((a, b) => a.name.localeCompare(b.name));
}

// ─── Parent / child tree ──────────────────────────────────────────────

export interface SpatialTree {
  /** Top-level rows, in sort order. */
  roots: DecoratedRow[];
  /** Visible children keyed by parent id, in sort order. */
  childrenByParent: Map<string, DecoratedRow[]>;
  /** Rows whose parent id points at an object we never loaded. */
  orphans: DecoratedRow[];
}

/**
 * Group the visible rows into a parent/child tree.
 *
 * A visible row nests under its parent when that parent is also
 * visible. When the parent exists but the current filter hid it, the
 * child is promoted to the top level rather than disappearing — hiding
 * a floor because its building didn't match the search would be worse
 * than showing it standalone. A row whose parent id resolves to nothing
 * in the loaded set is a genuine orphan and gets its own group so the
 * operator can spot broken links.
 */
export function buildTree(
  visible: DecoratedRow[],
  allRows: SpatialObjectAdminRow[],
): SpatialTree {
  const loadedIds = new Set(allRows.map((r) => r.id));
  const visibleIds = new Set(visible.map((d) => d.row.id));

  const roots: DecoratedRow[] = [];
  const orphans: DecoratedRow[] = [];
  const childrenByParent = new Map<string, DecoratedRow[]>();

  for (const d of visible) {
    const pid = d.row.parentObjectId;
    if (!pid) {
      roots.push(d);
    } else if (!loadedIds.has(pid)) {
      orphans.push(d);
    } else if (visibleIds.has(pid)) {
      const list = childrenByParent.get(pid);
      if (list) list.push(d);
      else childrenByParent.set(pid, [d]);
    } else {
      roots.push(d);
    }
  }

  return { roots, childrenByParent, orphans };
}

/**
 * Candidate parents for a child of `childType`, nearest first. Excludes
 * the object being edited and its own descendants so the operator can't
 * build a cycle.
 */
export function parentCandidates(
  decorated: DecoratedRow[],
  childType: SpatialObjectType,
  selfId: string | undefined,
): DecoratedRow[] {
  const allowed = PARENT_TYPES_FOR[childType];
  const blocked = new Set<string>();
  if (selfId) {
    blocked.add(selfId);
    // Walk down from self so a building can't be re-parented to its own floor.
    let frontier = [selfId];
    while (frontier.length) {
      const next: string[] = [];
      for (const d of decorated) {
        const pid = d.row.parentObjectId;
        if (pid && frontier.includes(pid) && !blocked.has(d.row.id)) {
          blocked.add(d.row.id);
          next.push(d.row.id);
        }
      }
      frontier = next;
    }
  }

  return decorated
    .filter((d) => {
      if (blocked.has(d.row.id)) return false;
      if (allowed && !allowed.includes(d.row.objectType)) return false;
      return true;
    })
    .sort((a, b) => {
      if (a.inView !== b.inView) return a.inView ? -1 : 1;
      const da = a.distanceM ?? Number.POSITIVE_INFINITY;
      const db = b.distanceM ?? Number.POSITIVE_INFINITY;
      if (da !== db) return da - db;
      return (a.row.name ?? '').localeCompare(b.row.name ?? '');
    });
}

/**
 * The candidate whose footprint covers `point`, preferring the tightest
 * one so clicking inside a floor that sits inside a building picks the
 * floor. Bbox containment rather than true point-in-polygon: the
 * candidate set is already type-filtered and small, and an approximate
 * hit the operator can see and correct beats pulling in a geometry
 * library for a pick affordance.
 */
export function pickRowAt(
  candidates: DecoratedRow[],
  point: LngLat,
): DecoratedRow | null {
  let best: DecoratedRow | null = null;
  let bestArea = Number.POSITIVE_INFINITY;
  for (const d of candidates) {
    const b = geometryBounds(d.row.geometry);
    if (!b || !pointInBounds(point, b)) continue;
    const area = Math.abs(b.east - b.west) * Math.abs(b.north - b.south);
    if (area < bestArea) { bestArea = area; best = d; }
  }
  return best;
}

/** Next free floor number under a parent (max existing + 1). */
export function nextFloorNumber(
  allRows: SpatialObjectAdminRow[],
  parentId: string,
): number {
  let max = 0;
  let seen = false;
  for (const r of allRows) {
    if (r.parentObjectId !== parentId) continue;
    const n = Number(r.floorNumber);
    if (!Number.isFinite(n)) continue;
    seen = true;
    if (n > max) max = n;
  }
  return seen ? max + 1 : 1;
}
